import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Copy, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const SEVERITY_STYLES = {
  critical: 'bg-red-100 text-red-700 border-red-200',
  security: 'bg-orange-100 text-orange-700 border-orange-200',
  warning: 'bg-amber-50 text-amber-700 border-amber-200',
};

export const SeverityBadge = ({ severity, status }) => (
  <span className={`px-2 py-0.5 rounded text-xs font-medium border ${SEVERITY_STYLES[severity] || 'bg-gray-100 text-gray-700'}`}
    data-testid="error-severity-badge">
    {status} · {severity}
  </span>
);

const Field = ({ label, value, mono }) => (value === null || value === undefined || value === '' ? null : (
  <div className="min-w-0">
    <dt className="text-[11px] uppercase tracking-wide text-gray-400">{label}</dt>
    <dd className={`text-xs text-gray-700 dark:text-gray-300 break-all ${mono ? 'font-mono' : ''}`}>{String(value)}</dd>
  </div>
));

const JsonBlock = ({ label, data }) => {
  if (!data || (typeof data === 'object' && Object.keys(data).length === 0)) return null;
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">{label}</div>
      <pre className="text-xs bg-gray-50 dark:bg-gray-900 border rounded p-2 overflow-x-auto max-h-48 whitespace-pre-wrap break-all">
        {typeof data === 'string' ? data : JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
};

const ErrorLogDetail = ({ log }) => {
  const [open, setOpen] = useState(false);
  const md = log.metadata || {};
  const copy = () => {
    navigator.clipboard.writeText(JSON.stringify(log, null, 2));
    toast.success('Error entry copied');
  };
  return (
    <div className="mt-2" data-testid={`error-detail-${log.id}`}>
      <button type="button" onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800"
        data-testid={`error-detail-toggle-${log.id}`}>
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        {open ? 'Hide details' : 'Show details'}
      </button>
      {open && (
        <div className="mt-2 space-y-3 border-l-2 border-red-200 pl-3" data-testid={`error-detail-body-${log.id}`}>
          <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Field label="Module" value={md.module} />
            <Field label="Request" value={`${md.method} ${md.path}`} mono />
            <Field label="Route" value={md.route} mono />
            <Field label="Status" value={md.status_code} />
            <Field label="Request ID" value={md.request_id} mono />
            <Field label="Duration" value={md.duration_ms != null ? `${md.duration_ms} ms` : null} />
            <Field label="Exception" value={md.exception_type} mono />
            <Field label="User role" value={md.user_role} />
            <Field label="User agent" value={md.user_agent} />
            <Field label="Referer" value={md.referer} />
            <Field label="Claimed tenant" value={md.claimed_tenant_id} mono />
            <Field label="Occurrences" value={md.occurrences > 1 ? `${md.occurrences} (last ${new Date(md.last_seen).toLocaleString()})` : null} />
          </dl>
          <JsonBlock label="Error message" data={md.error_message} />
          <JsonBlock label="Query params" data={md.query_params} />
          <JsonBlock label="Path params" data={md.path_params} />
          <JsonBlock label="Request body (secrets removed)" data={md.request_body} />
          <JsonBlock label="Stack trace" data={md.traceback} />
          {md.traces_hidden && (
            <p className="flex items-center gap-1 text-xs text-gray-500" data-testid="error-traces-hidden">
              <EyeOff className="w-3.5 h-3.5" />Stack trace and request body are visible to tenant owners/admins only.
            </p>
          )}
          <button type="button" onClick={copy} className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900"
            data-testid={`error-copy-${log.id}`}>
            <Copy className="w-3.5 h-3.5" />Copy JSON
          </button>
        </div>
      )}
    </div>
  );
};

export default ErrorLogDetail;
